// src/pages/Admin/Products/index.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import StockAdjustmentModal from '../../../components/admin/StockAdjustmentModal'
import { hasAnyRole } from '../../../utils/roleUtils';
import type { Product, ProductVariant, Warehouse } from '../../../types';
import ProductFormModal from '../../../components/products/ProductFormModal';
import DeleteProductModal from '../../../components/products/DeleteProductModal';
import { adminAPI } from '../../../api/admin';
import { activityLogger } from '../../../utils/activityLogger';
import { stockAPI } from '../../../api/stock';
import ProductList from '../../../components/products/ProductList';
import { useProductFilters } from '../../../hooks/useProductFilters';
import toast from '../../../utils/toast';

const ProductsPage: React.FC = () => {
    const { user } = useAuth();
    // const [products, setProducts] = useState<Product[]>([]);
    // const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [categories, setCategories] = useState<string[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewMode, setReviewMode] = useState<'view' | 'review'>('review');
    const [stockModal, setStockModal] = useState<{
        isOpen: boolean;
        product: Product | null;
        variants: ProductVariant[];
    }>({
        isOpen: false,
        product: null,
        variants: []
    });
    const {
        products,
        loading,
        pagination,
        statusFilter,
        searchTerm,
        handleStatusFilterChange,
        handleSearchChange,
        handlePageChange,
        fetchData,
        setProducts,
    } = useProductFilters({
        fetchProducts: async (page, status, search) => {
            const params: any = {
                page: page,
                page_size: 20,
            };

            // ✅ Admin can see all statuses including archived
            if (status === 'archived') {
                params.status = 'archived';
            } else if (status !== 'all' && status !== 'active') {
                params.status = status;
            }

            if (search?.trim()) {
                params.search = search.trim();
            }

            // ✅ Admin doesn't need vendor filter (shows all vendors)
            const response = await adminAPI.getProducts(params);
            return {
                data: response.data?.results || response.data || [],
                total: response.data?.count || 0,
                pages: Math.ceil((response.data?.count || 0) / 20),
            };
        },
        initialStatus: 'all',
    });
    const canManage = user && hasAnyRole(user.roles, ['admin', 'manager']);

    useEffect(() => {
        const fetchWarehouses = async () => {
            const response = await stockAPI.getWarehouses();
            if (!response.error) {
                setWarehouses(response.data || []);
            }
        };
        fetchWarehouses();
    }, []);

    const handleDelete = async (productId: number, productTitle: string) => {
        try {
            // You'll need to add delete endpoint to productsAPI
            const response = await adminAPI.deleteProduct(productId);
            if (!response.error) {
                activityLogger.productDeleted(productId, productTitle);
                await fetchData(); // Refresh list
            }
            setShowDeleteModal(false);
            setSelectedProduct(null);
        } catch (error) {
            console.error('Failed to delete product:', error);
        }
    };

    const handleAdminRowClick = async (product: any) => {
        try {
            // ✅ Fetch the latest product data BEFORE opening modal
            const response = await adminAPI.getProduct(product.id);
            const updatedProduct = response.data;

            // ✅ Update the product in the list
            setProducts(prevProducts =>
                prevProducts.map(p =>
                    p.id === updatedProduct.id ? updatedProduct : p
                )
            );

            // ✅ Determine mode based on status
            const mode = updatedProduct.status === 'pending_review' ? 'review' : 'view';

            // ✅ Now open the modal with the updated product
            setSelectedProduct(updatedProduct);
            setReviewMode(mode);
            setShowReviewModal(true);
        } catch (error) {
            console.error('Failed to fetch product:', error);
            toast.error('Failed to load product details');
            // Fallback to existing product if fetch fails
            const mode = product.status === 'pending_review' ? 'review' : 'view';
            setSelectedProduct(product);
            setReviewMode(mode);
            setShowReviewModal(true);
        }
    };

    return (
        <div className="space-y-6">
            {/* Products Table */}
            <ProductList
                products={products}
                loading={loading}
                onRefresh={fetchData}
                showVendorColumn={true}
                actions={['view', 'review', 'stock']}
                onProductView={(product) => {
                    setSelectedProduct(product);
                    setReviewMode('view');
                    setShowReviewModal(true);
                }}
                onProductReview={(product) => {
                    setSelectedProduct(product);
                    setReviewMode('review');
                    setShowReviewModal(true);
                }}
                onStockAdjust={(product) => {
                    setStockModal({ isOpen: true, product });
                }}
                onRowClick={handleAdminRowClick}
                title={`Product Management`}
                subtitle="Manage and review all products across all vendors"
                showAddButton={false}
                emptyMessage="This vendor has no products yet"
                pagination={pagination}
                onPageChange={handlePageChange}
                onFilterChange={handleStatusFilterChange}
                onSearchChange={handleSearchChange}
                isAdmin={true}
                showStoreColumn={true}
                statusFilter={statusFilter}
            />

            {/* Modals */}
            {showReviewModal && selectedProduct && (
                <ProductFormModal
                    product={selectedProduct}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedProduct(null);
                    }}
                    onSuccess={() => {
                        fetchData();
                        setShowReviewModal(false);
                        setSelectedProduct(null);
                    }}
                    mode={reviewMode}
                    isStaff={true}
                />
            )}

            {showDeleteModal && selectedProduct && (
                <DeleteProductModal
                    product={selectedProduct}
                    onConfirm={() => handleDelete(selectedProduct.id, selectedProduct.title)}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setSelectedProduct(null);
                    }}
                />
            )}

            {stockModal.isOpen &&
                <StockAdjustmentModal
                    isOpen={stockModal.isOpen}
                    onClose={() => setStockModal({ isOpen: false, product: null, variants: [] })}
                    onSuccess={fetchData} // Refresh your product list
                    product={stockModal.product!}
                    variants={stockModal.variants}
                    warehouses={warehouses}
                />
            }
        </div>
    );
};

export default ProductsPage;